import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  DollarSign, Settings, Wallet, Loader2, History, TrendingUp, Save,
  Percent, Coins, Info, ChevronRight, ShieldCheck, Sparkles,
  ArrowUpRight, CheckCircle2, XCircle,
} from 'lucide-react';

function money(n) {
  const num = Number(n || 0);
  return `$${num.toFixed(2)}`;
}

/** A group of numeric inputs — used repeatedly for the many percent/USD knobs. */
function KnobGroup({ title, description, knobs, values, dirty, onChange, testIdPrefix }) {
  return (
    <Card className="p-5 rounded-2xl" data-testid={`monet-group-${testIdPrefix}`}>
      <h3 className="font-bold mb-1">{title}</h3>
      {description && <p className="text-xs text-muted-foreground mb-4">{description}</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        {knobs.map(k => (
          <div key={k.field} className="space-y-1.5">
            <Label htmlFor={k.field} className="text-xs">{k.label}</Label>
            <div className="relative">
              {k.unit === '$' && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              )}
              <Input
                id={k.field}
                type="number"
                step={k.step || (k.unit === '%' ? 1 : 0.01)}
                min={0}
                max={k.unit === '%' ? 100 : undefined}
                value={values[k.field] ?? ''}
                onChange={(e) => onChange(k.field, e.target.value)}
                className={`${k.unit === '$' ? 'pl-6' : ''} ${k.unit === '%' ? 'pr-8' : ''} ${dirty.has(k.field) ? 'border-violet-500' : ''}`}
                data-testid={`monet-input-${k.field}`}
              />
              {k.unit === '%' && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
              )}
            </div>
            {k.hint && <p className="text-[10px] text-muted-foreground/70">{k.hint}</p>}
          </div>
        ))}
      </div>
    </Card>
  );
}

// The 17 monetization sources shown as toggles
const SOURCE_TOGGLES = [
  { key: 'qualified_views', label: 'Qualified Views' },
  { key: 'ads', label: 'Advertisements' },
  { key: 'premium_subs', label: 'Premium Subscriber Rev' },
  { key: 'tips', label: 'Tips' },
  { key: 'coins', label: 'Virtual Coins' },
  { key: 'gifts', label: 'Virtual Gifts' },
  { key: 'subscribers', label: 'Paid Subscribers' },
  { key: 'ppv', label: 'Pay-Per-View' },
  { key: 'exclusive', label: 'Exclusive Content' },
  { key: 'livestream_tickets', label: 'Livestream Tickets' },
  { key: 'store', label: 'Creator Store' },
  { key: 'merchandise', label: 'Merchandise' },
  { key: 'affiliates', label: 'Affiliate Sales' },
  { key: 'brand_deals', label: 'Brand Sponsorships' },
  { key: 'referrals', label: 'Referral Program' },
  { key: 'monthly_bonus', label: 'Monthly Bonus Pool' },
  { key: 'achievements', label: 'Achievement Bonuses' },
];

// ═══════════════════════════════════════════════════════
// Admin Cashouts Panel — approve/reject cashout requests
// ═══════════════════════════════════════════════════════
function AdminCashoutsPanel() {
  const { getAuthHeaders } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/cashouts/pending`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRequests(data.requests || []);
    } catch {
      toast.error('Could not load cashouts');
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const approve = async (req_id, method) => {
    if (!window.confirm(`Approve this cashout via ${method === 'stripe' ? 'Stripe payout' : 'manual'}?`)) return;
    setProcessing(req_id);
    try {
      const res = await fetch(`${API}/admin/cashouts/${req_id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ method }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Approve failed');
      } else {
        const data = await res.json();
        toast.success(data.stripe_transfer_id
          ? `Approved · Stripe transfer ${data.stripe_transfer_id}`
          : 'Approved (manual)');
        await load();
      }
    } catch {
      toast.error('Approve failed');
    }
    setProcessing(null);
  };

  const reject = async (req_id) => {
    const reason = window.prompt('Reject reason?');
    if (!reason) return;
    setProcessing(req_id);
    try {
      const res = await fetch(`${API}/admin/cashouts/${req_id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error();
      toast.success('Rejected');
      await load();
    } catch {
      toast.error('Reject failed');
    }
    setProcessing(null);
  };

  return (
    <Card className="p-5 rounded-2xl" data-testid="admin-cashouts-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold flex items-center gap-2">
            <ArrowUpRight className="w-4 h-4 text-emerald-400" />
            Pending Cashout Requests
          </h3>
          <p className="text-xs text-muted-foreground">Approve to send real Stripe payouts.</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="cashouts-refresh">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
        </Button>
      </div>
      {requests.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground" data-testid="cashouts-empty">
          <ShieldCheck className="w-8 h-8 mx-auto opacity-40 mb-2" />
          No pending cashouts.
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map(r => {
            const u = r.user || {};
            const connectReady = u.stripe_connect_status === 'enabled';
            return (
              <div key={r.request_id} className="p-4 rounded-xl border border-white/10 bg-card/40" data-testid={`admin-cashout-${r.request_id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt={u.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center font-black text-white">
                        {(u.name || u.handle || '?')[0]}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{u.name || u.handle || u.user_id}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{u.email || u.user_id}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-black">{money(r.amount_usd)}</p>
                    <p className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                {r.method && (
                  <p className="text-[11px] mb-2">
                    Method: <span className="font-mono">{r.method}</span>
                    {r.payout_email ? <> · <span className="font-mono">{r.payout_email}</span></> : null}
                  </p>
                )}
                {r.notes && <p className="text-[11px] text-muted-foreground italic mb-2">&ldquo;{r.notes}&rdquo;</p>}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/5">
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    connectReady ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'
                  }`}>
                    {connectReady ? 'Stripe Ready' : 'No Connect Account'}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => reject(r.request_id)}
                      disabled={processing === r.request_id}
                      data-testid={`reject-${r.request_id}`}
                    >
                      <XCircle className="w-3 h-3 mr-1" /> Reject
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => approve(r.request_id, 'manual')}
                      disabled={processing === r.request_id}
                      variant="outline"
                      data-testid={`approve-manual-${r.request_id}`}
                    >
                      Mark Paid (manual)
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => approve(r.request_id, 'stripe')}
                      disabled={processing === r.request_id || !connectReady}
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                      data-testid={`approve-stripe-${r.request_id}`}
                    >
                      {processing === r.request_id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <><CheckCircle2 className="w-3 h-3 mr-1" /> Approve · Stripe</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}


export default function AdminMonetizationPage() {
  const { getAuthHeaders, isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [overview, setOverview] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    try {
      const [cRes, oRes, pRes, hRes] = await Promise.all([
        fetch(`${API}/admin/monetization/config`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/admin/monetization/revenue-overview?days=30`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/admin/monetization/reward-pool/preview`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/admin/monetization/config-history?limit=20`, { headers }).then(r => r.ok ? r.json() : { items: [] }),
      ]);
      setConfig(cRes);
      setDraft(cRes || {});
      setOverview(oRes);
      setPreview(pRes);
      setHistory(hRes?.items || []);
      setDirty(new Set());
    } catch {
      toast.error('Failed to load monetization admin');
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    // Trust backend enforcement — the admin-monetization endpoints already require_admin
    loadAll();
  }, [authLoading, isAuthenticated, loadAll, navigate]);

  const updateKnob = (field, val) => {
    const parsed = val === '' ? null : Number(val);
    setDraft(d => ({ ...d, [field]: parsed }));
    setDirty(s => new Set(s).add(field));
  };

  const toggleSource = (key, val) => {
    setDraft(d => ({
      ...d,
      sources_enabled: { ...(d.sources_enabled || {}), [key]: val },
    }));
    setDirty(s => new Set(s).add('sources_enabled'));
  };

  const save = async () => {
    if (!dirty.size) return;
    setSaving(true);
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    const payload = {};
    for (const f of dirty) {
      payload[f] = draft[f];
    }
    try {
      const res = await fetch(`${API}/admin/monetization/config`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Save failed' }));
        toast.error(err.detail || 'Save failed');
      } else {
        toast.success('Monetization config updated');
        await loadAll();
      }
    } catch {
      toast.error('Network error');
    }
    setSaving(false);
  };

  if (loading || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground pt-24 pb-16" data-testid="admin-monetization-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Monetization Control</h1>
              <p className="text-xs text-muted-foreground">One place to tune every fee, share, threshold, and toggle.</p>
            </div>
          </div>
          <Button
            onClick={save}
            disabled={!dirty.size || saving}
            className="bg-gradient-to-r from-violet-600 to-fuchsia-600 gap-1.5"
            data-testid="monet-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes {dirty.size ? `(${dirty.size})` : ''}
          </Button>
        </div>

        {/* Live overview */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Card className="p-4 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-card/40 border-emerald-500/25" data-testid="monet-overview-revenue">
            <div className="flex items-center gap-2 mb-1"><DollarSign className="w-3.5 h-3.5 text-emerald-400" /><p className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">Platform Rev (30d)</p></div>
            <p className="text-2xl font-bold">{money(overview?.total_platform_revenue_usd)}</p>
          </Card>
          <Card className="p-4 rounded-2xl bg-gradient-to-br from-violet-500/10 to-card/40 border-violet-500/25" data-testid="monet-overview-pool">
            <div className="flex items-center gap-2 mb-1"><Wallet className="w-3.5 h-3.5 text-violet-400" /><p className="text-[10px] uppercase tracking-widest text-violet-400 font-semibold">Reward Pool</p></div>
            <p className="text-2xl font-bold">{money(overview?.reward_pool_usd)}</p>
            <p className="text-[10px] text-muted-foreground">{overview?.reward_pool_percent}% of platform rev</p>
          </Card>
          <Card className="p-4 rounded-2xl bg-gradient-to-br from-orange-500/10 to-card/40 border-orange-500/25" data-testid="monet-overview-qv">
            <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-3.5 h-3.5 text-orange-400" /><p className="text-[10px] uppercase tracking-widest text-orange-400 font-semibold">Qualified Views</p></div>
            <p className="text-2xl font-bold">{(overview?.qualified_views_count || 0).toLocaleString()}</p>
          </Card>
          <Card className="p-4 rounded-2xl bg-gradient-to-br from-fuchsia-500/10 to-card/40 border-fuchsia-500/25" data-testid="monet-overview-perstream">
            <div className="flex items-center gap-2 mb-1"><Coins className="w-3.5 h-3.5 text-fuchsia-400" /><p className="text-[10px] uppercase tracking-widest text-fuchsia-400 font-semibold">Per-Stream Value</p></div>
            <p className="text-2xl font-bold">${(overview?.per_stream_value_usd || 0).toFixed(4)}</p>
            <p className="text-[10px] text-muted-foreground">Auto — reward pool ÷ QV</p>
          </Card>
        </div>

        {/* Today preview */}
        {preview && (
          <Card className="p-4 mb-6 rounded-2xl border-violet-500/25 bg-gradient-to-r from-violet-500/5 via-card/40 to-card/40" data-testid="monet-preview-today">
            <div className="flex items-center justify-between flex-wrap gap-3 text-xs">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-violet-400" />
                <span className="text-muted-foreground">Today&rsquo;s live preview ({preview.date}):</span>
              </div>
              <span>Platform rev <b className="text-foreground">{money(preview.today_platform_revenue_usd)}</b></span>
              <span>Pool <b className="text-foreground">{money(preview.reward_pool_usd)}</b></span>
              <span>QV <b className="text-foreground">{(preview.qualified_views_today || 0).toLocaleString()}</b></span>
              <span>Per view <b className="text-foreground">${preview.per_stream_value_usd.toFixed(6)}</b></span>
            </div>
          </Card>
        )}

        <Tabs defaultValue="revenue">
          <TabsList className="bg-card/60 border border-border mb-4">
            <TabsTrigger value="revenue" data-testid="monet-tab-revenue">Revenue Share</TabsTrigger>
            <TabsTrigger value="pool" data-testid="monet-tab-pool">Reward Pool</TabsTrigger>
            <TabsTrigger value="rewards" data-testid="monet-tab-rewards">Rewards</TabsTrigger>
            <TabsTrigger value="sources" data-testid="monet-tab-sources">Sources</TabsTrigger>
            <TabsTrigger value="cashouts" data-testid="monet-tab-cashouts">Cashouts</TabsTrigger>
            <TabsTrigger value="history" data-testid="monet-tab-history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="revenue" className="space-y-4">
            <KnobGroup
              title="Platform Fees"
              description="What % of each transaction the platform keeps. Creators receive the remainder."
              testIdPrefix="fees"
              values={draft}
              dirty={dirty}
              onChange={updateKnob}
              knobs={[
                { field: 'tip_fee_percent', label: 'Tips', unit: '%' },
                { field: 'gift_fee_percent', label: 'Gifts', unit: '%' },
                { field: 'coin_fee_percent', label: 'Coins', unit: '%' },
                { field: 'ppv_fee_percent', label: 'Pay-Per-View', unit: '%' },
                { field: 'subscription_fee_percent', label: 'Subscriptions', unit: '%' },
                { field: 'store_fee_percent', label: 'Creator Store', unit: '%' },
                { field: 'marketplace_fee_percent', label: 'Marketplace', unit: '%' },
              ]}
            />
            <KnobGroup
              title="Payouts"
              testIdPrefix="payouts"
              values={draft}
              dirty={dirty}
              onChange={updateKnob}
              knobs={[
                { field: 'min_payout_usd', label: 'Minimum Payout', unit: '$', hint: 'Below this amount creators cannot cash out.' },
                { field: 'payout_hold_days', label: 'Payout Hold (days)', hint: 'How long earnings sit as "pending" before becoming available.' },
              ]}
            />
          </TabsContent>

          <TabsContent value="pool" className="space-y-4">
            <KnobGroup
              title="Creator Reward Pool"
              description="Portion of platform revenue that is redistributed to creators based on qualified streams. Set to 0 to disable."
              testIdPrefix="pool"
              values={draft}
              dirty={dirty}
              onChange={updateKnob}
              knobs={[
                { field: 'creator_pool_percent', label: 'Creator Pool %', unit: '%', hint: 'e.g. 60 = 60% of platform revenue enters the pool' },
                { field: 'monthly_bonus_pool_usd', label: 'Monthly Bonus Pool ($)', unit: '$', hint: 'Extra bonus pool split across top creators each month.' },
              ]}
            />
            <KnobGroup
              title="Qualified View Rules"
              description="A view only counts when the viewer watches ≥ these thresholds."
              testIdPrefix="qv"
              values={draft}
              dirty={dirty}
              onChange={updateKnob}
              knobs={[
                { field: 'qualified_view_seconds', label: 'Minimum Seconds Watched' },
                { field: 'qualified_view_percent', label: 'OR % of Video Watched', unit: '%' },
              ]}
            />

            {/* Reward pool recompute */}
            <Card className="p-5 rounded-2xl" data-testid="reward-pool-runner">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h3 className="font-bold mb-1">Run Daily Reward Pool</h3>
                  <p className="text-xs text-muted-foreground max-w-md">Recomputes yesterday&rsquo;s reward pool → per-stream value → credits every creator&rsquo;s wallet based on their qualified views. Safe to click — idempotent per day.</p>
                </div>
                <Button
                  onClick={async () => {
                    const res = await fetch(`${API}/views/admin/recompute-pool`, {
                      method: 'POST',
                      headers: getAuthHeaders(),
                    });
                    const data = await res.json();
                    if (data.success) {
                      toast.success(`Reward pool ran — credited ${data.run.creators_credited} creators, $${data.run.total_credited_usd} total`);
                    } else if (data.reason === 'already_run') {
                      toast.info('Reward pool already ran for that day');
                    } else {
                      toast.error(data.detail || 'Recompute failed');
                    }
                  }}
                  className="bg-gradient-to-r from-violet-600 to-fuchsia-600 gap-1.5"
                  data-testid="reward-pool-run-btn"
                >
                  <Sparkles className="w-4 h-4" /> Run for Yesterday
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="rewards" className="space-y-4">
            <KnobGroup
              title="Referral Rewards"
              testIdPrefix="referral"
              values={draft}
              dirty={dirty}
              onChange={updateKnob}
              knobs={[
                { field: 'referral_reward_usd', label: 'Reward per Referral', unit: '$' },
              ]}
            />
            <KnobGroup
              title="Coin Economy"
              testIdPrefix="coins"
              values={draft}
              dirty={dirty}
              onChange={updateKnob}
              knobs={[
                { field: 'coin_pack_usd_per_100', label: 'Price per 100 coins', unit: '$' },
                { field: 'coin_to_usd_rate', label: 'Coin → USD rate', unit: '$', hint: 'Used for creator payouts. Example: 0.005 = 200 coins → $1' },
              ]}
            />
          </TabsContent>

          <TabsContent value="sources">
            <Card className="p-5 rounded-2xl" data-testid="monet-sources-toggles">
              <h3 className="font-bold mb-1">Revenue Sources</h3>
              <p className="text-xs text-muted-foreground mb-4">Toggle any revenue stream on/off platform-wide. When off, creators won&rsquo;t see it in the wallet breakdown.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {SOURCE_TOGGLES.map(s => {
                  const on = draft?.sources_enabled?.[s.key] !== false;
                  return (
                    <label key={s.key} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-border hover:bg-muted/20 cursor-pointer" data-testid={`monet-toggle-${s.key}`}>
                      <span className="text-sm font-medium">{s.label}</span>
                      <Switch checked={on} onCheckedChange={(v) => toggleSource(s.key, v)} />
                    </label>
                  );
                })}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="cashouts">
            <AdminCashoutsPanel />
          </TabsContent>

          <TabsContent value="history">
            <Card className="p-5 rounded-2xl" data-testid="monet-history">
              <h3 className="font-bold mb-1 flex items-center gap-2"><History className="w-4 h-4" /> Config Audit Log</h3>
              <p className="text-xs text-muted-foreground mb-4">Every change to monetization config is logged.</p>
              <ul className="divide-y divide-border/50">
                {history.map(h => (
                  <li key={h.history_id} className="py-3" data-testid={`monet-history-${h.history_id}`}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="text-xs font-semibold">{h.changed_by_name || 'Admin'}</span>
                      <span className="text-[10px] text-muted-foreground">{new Date(h.changed_at).toLocaleString()}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(h.changes || {}).map(([k, v]) => (
                        <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/25">
                          {k}: {typeof v === 'object' ? '(sources)' : String(v)}
                        </span>
                      ))}
                    </div>
                  </li>
                ))}
                {!history.length && (
                  <li className="py-6 text-center text-muted-foreground text-xs" data-testid="monet-history-empty">
                    No changes yet.
                  </li>
                )}
              </ul>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer helper */}
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground/70 mt-6 justify-center">
          <ShieldCheck className="w-3 h-3" />
          Changes take effect immediately for all future transactions. Existing pending earnings are not retroactively re-priced.
        </div>
      </div>
    </div>
  );
}
